import type { FeatureKeyOf, ModuleKey } from './module-catalog.js';

/// Catálogo puro de permissões e seus donos comerciais.
export interface PermissionDefinition {
  key: string;
  /// O módulo dono da permissão. É o que liga a permissão à contratação: sem o módulo, a permissão
  /// não vale nada por mais que o perfil a conceda.
  moduleKey: string;
  /// A parte vendável do módulo, quando a permissão pertence a uma. `null` = miolo do módulo,
  /// incluído sempre que o módulo estiver contratado.
  featureKey: string | null;
  /// Rótulo de agrupamento na tela de perfis. É só apresentação — "Sócios" e "Investimento inicial"
  /// aparecem separados para o operador, mas são o mesmo produto.
  category: string;
  displayName: string;
  version: number;
}

function permission<
  const Key extends string,
  const Owner extends ModuleKey,
  const Feature extends FeatureKeyOf<Owner> | null = null,
>(
  key: Key,
  moduleKey: Owner,
  category: string,
  displayName: string,
  featureKey: Feature = null as Feature,
): Readonly<PermissionDefinition & { key: Key; moduleKey: Owner; featureKey: Feature }> {
  return Object.freeze({ key, moduleKey, featureKey, category, displayName, version: 1 });
}

export const permissionCatalog = Object.freeze([
  permission('clients.read', 'clients', 'Clientes', 'Visualizar clientes'),
  permission('clients.create', 'clients', 'Clientes', 'Cadastrar clientes'),
  permission('clients.update', 'clients', 'Clientes', 'Editar clientes'),
  permission('clients.delete', 'clients', 'Clientes', 'Excluir clientes'),
  permission(
    'clients.access.manage',
    'clients',
    'Clientes',
    'Gerenciar acesso de clientes ao portal',
  ),
  permission('client_services.link', 'clients', 'Clientes', 'Vincular serviços a clientes'),
  permission('client_services.unlink', 'clients', 'Clientes', 'Desvincular serviços de clientes'),
  permission('services.read', 'services', 'Serviços', 'Visualizar serviços'),
  permission('services.create', 'services', 'Serviços', 'Cadastrar serviços'),
  permission('services.update', 'services', 'Serviços', 'Editar serviços'),
  permission('services.delete', 'services', 'Serviços', 'Excluir serviços'),
  permission('prepaid_balances.read', 'prepaid-balances', 'Saldos pré-pagos', 'Ver saldos'),
  permission('prepaid_balances.use', 'prepaid-balances', 'Saldos pré-pagos', 'Usar e desfazer uso'),
  permission(
    'prepaid_balances.adjust',
    'prepaid-balances',
    'Saldos pré-pagos',
    'Bonificar, corrigir e prorrogar',
  ),
  permission(
    'prepaid_balances.settings.manage',
    'prepaid-balances',
    'Saldos pré-pagos',
    'Configurar regras',
  ),
  permission(
    'online_payments.settings.manage',
    'online-payments',
    'Cobrança online',
    'Configurar cobrança online',
  ),
  permission('sales.read', 'sales', 'Vendas', 'Visualizar vendas, pacotes e promoções'),
  permission('sales.packages.manage', 'sales', 'Vendas', 'Gerenciar pacotes de venda'),
  permission('sales.promotions.manage', 'sales', 'Vendas', 'Gerenciar promoções de venda'),
  permission('sales.create', 'sales', 'Vendas', 'Registrar vendas e pagamentos'),
  permission('sales.cancel', 'sales', 'Vendas', 'Cancelar vendas'),
  permission(
    'sales.payments.manage',
    'sales',
    'Vendas',
    'Registrar e confirmar pagamentos de vendas',
  ),
  permission('sales.settings.manage', 'sales', 'Vendas', 'Configurar comprovantes de pagamento'),
  permission(
    'sales.commissions.read',
    'sales',
    'Vendas',
    'Ver valores de comissão e a apuração',
    'sales.commissions',
  ),
  permission(
    'sales.commissions.manage',
    'sales',
    'Vendas',
    'Configurar regras de comissão',
    'sales.commissions',
  ),
  permission(
    'sales.commissions.reassign',
    'sales',
    'Vendas',
    'Trocar quem recebe a comissão depois da venda',
    'sales.commissions',
  ),
  permission(
    'sales.commissions.close',
    'sales',
    'Vendas',
    'Registrar vales, apurar e fechar comissões',
    'sales.commissions',
  ),
  permission(
    'sales.commissions.read_own',
    'sales',
    'Vendas',
    'Ver as próprias comissões',
    'sales.commissions',
  ),
  permission('inventory.read', 'inventory', 'Estoque', 'Visualizar estoque'),
  permission('inventory.products.manage', 'inventory', 'Estoque', 'Gerenciar produtos'),
  permission('inventory.locations.manage', 'inventory', 'Estoque', 'Gerenciar locais'),
  permission('inventory.movements.create', 'inventory', 'Estoque', 'Registrar movimentações'),
  permission('inventory.movements.reverse', 'inventory', 'Estoque', 'Estornar movimentações'),
  permission('inventory.replenishment.manage', 'inventory', 'Estoque', 'Gerenciar reposição'),
  permission('purchasing.read', 'purchasing', 'Compras', 'Visualizar compras'),
  permission('purchasing.suppliers.manage', 'purchasing', 'Compras', 'Gerenciar fornecedores'),
  permission('purchasing.offerings.manage', 'purchasing', 'Compras', 'Gerenciar ofertas'),
  permission('purchasing.orders.manage', 'purchasing', 'Compras', 'Gerenciar pedidos de compra'),
  permission('purchasing.receipts.manage', 'purchasing', 'Compras', 'Registrar recebimentos'),
  permission('scheduling.read', 'scheduling', 'Agendamento', 'Visualizar agenda'),
  permission('scheduling.create', 'scheduling', 'Agendamento', 'Criar agendamentos'),
  permission('scheduling.update', 'scheduling', 'Agendamento', 'Remarcar agendamentos'),
  permission('scheduling.cancel', 'scheduling', 'Agendamento', 'Cancelar agendamentos'),
  permission('scheduling.complete', 'scheduling', 'Agendamento', 'Concluir e reabrir atendimentos'),
  permission(
    'scheduling.resources.manage',
    'scheduling',
    'Agendamento',
    'Gerenciar profissionais e salas',
  ),
  permission(
    'scheduling.blocks.manage',
    'scheduling',
    'Agendamento',
    'Gerenciar bloqueios da agenda',
  ),
  permission(
    'scheduling.settings.manage',
    'scheduling',
    'Agendamento',
    'Configurar sinal, portal e cancelamento do agendamento',
  ),
  permission('protocols.read', 'protocols', 'Protocolos', 'Visualizar protocolos'),
  permission('protocols.create', 'protocols', 'Protocolos', 'Cadastrar protocolos'),
  permission('protocols.update', 'protocols', 'Protocolos', 'Editar protocolos'),
  permission('protocols.delete', 'protocols', 'Protocolos', 'Arquivar protocolos'),
  permission(
    'protocols.materials.read',
    'protocols',
    'Materiais dos protocolos',
    'Visualizar materiais previstos',
    'protocols.materials',
  ),
  permission(
    'protocols.materials.manage',
    'protocols',
    'Materiais dos protocolos',
    'Gerenciar materiais previstos',
    'protocols.materials',
  ),
  permission(
    'protocols.applications.read',
    'protocols',
    'Aplicação do protocolo',
    'Visualizar aplicações de protocolo',
    'protocols.applications',
  ),
  permission(
    'protocols.applications.manage',
    'protocols',
    'Aplicação do protocolo',
    'Planejar e aplicar protocolos',
    'protocols.applications',
  ),
  permission(
    'materials_usage.read',
    'inventory',
    'Consumo de materiais',
    'Visualizar consumo de materiais',
    'inventory.materials-usage',
  ),
  permission(
    'materials_usage.register',
    'inventory',
    'Consumo de materiais',
    'Registrar consumo de materiais',
    'inventory.materials-usage',
  ),
  permission(
    'materials_usage.reverse',
    'inventory',
    'Consumo de materiais',
    'Estornar consumo de materiais',
    'inventory.materials-usage',
  ),
  permission(
    'materials_usage.settings.manage',
    'inventory',
    'Consumo de materiais',
    'Definir o local padrão de consumo',
    'inventory.materials-usage',
  ),
  permission('records.read', 'records', 'Prontuário', 'Visualizar prontuário e Quadro de saúde'),
  permission(
    'records.manage',
    'records',
    'Prontuário',
    'Preencher fichas e registrar alertas da equipe',
  ),
  permission(
    'records.templates.manage',
    'records',
    'Prontuário',
    'Montar modelos de ficha e temas de saúde',
  ),
  permission('users.read', 'access', 'Pessoas e acessos', 'Visualizar pessoas e acessos'),
  permission('company_profile.read', 'access', 'Dados da empresa', 'Visualizar dados da empresa'),
  permission('company_profile.manage', 'access', 'Dados da empresa', 'Alterar dados da empresa'),
  permission('business_units.read', 'access', 'Unidades', 'Visualizar unidades'),
  permission('business_units.manage', 'access', 'Unidades', 'Gerenciar unidades'),
  permission('users.invite', 'access', 'Pessoas e acessos', 'Convidar pessoas'),
  permission(
    'users.create_with_password',
    'access',
    'Pessoas e acessos',
    'Criar acesso com senha inicial',
  ),
  permission(
    'users.reset_authenticator',
    'access',
    'Pessoas e acessos',
    'Redefinir autenticador de outra pessoa',
  ),
  permission('users.update', 'access', 'Pessoas e acessos', 'Editar acessos'),
  permission('users.deactivate', 'access', 'Pessoas e acessos', 'Desativar acessos'),
  permission('roles.read', 'access', 'Perfis de acesso', 'Visualizar perfis de acesso'),
  permission('roles.create', 'access', 'Perfis de acesso', 'Criar perfis de acesso'),
  permission('roles.update', 'access', 'Perfis de acesso', 'Editar perfis de acesso'),
  permission('roles.delete', 'access', 'Perfis de acesso', 'Excluir perfis de acesso'),
  permission('roles.assign', 'access', 'Perfis de acesso', 'Atribuir perfis de acesso'),
  permission('partners.read', 'investment', 'Sócios', 'Visualizar sócios'),
  permission('partners.create', 'investment', 'Sócios', 'Cadastrar sócios'),
  permission('partners.update', 'investment', 'Sócios', 'Editar sócios'),
  permission('partners.delete', 'investment', 'Sócios', 'Excluir sócios'),
  permission('guides.steps.confirm', 'access', 'Roteiros', 'Confirmar passos de roteiros'),
  permission('audit.read', 'audit', 'Auditoria', 'Visualizar auditoria'),
  permission('audit.export', 'audit', 'Auditoria', 'Exportar auditoria'),
  permission(
    'investment.read',
    'investment',
    'Investimento inicial',
    'Visualizar investimento inicial',
  ),
  permission(
    'investment.create',
    'investment',
    'Investimento inicial',
    'Cadastrar investimento inicial',
  ),
  permission(
    'investment.update',
    'investment',
    'Investimento inicial',
    'Editar investimento inicial',
  ),
  permission(
    'investment.delete',
    'investment',
    'Investimento inicial',
    'Excluir investimento inicial',
  ),
  permission(
    'investment.receipts.manage',
    'investment',
    'Investimento inicial',
    'Gerenciar comprovantes do investimento',
    'investment.receipts',
  ),
  permission(
    'investment.slips.manage',
    'investment',
    'Investimento inicial',
    'Gerenciar boletos do investimento',
    'investment.slips',
  ),
  permission(
    'investment.repayments.manage',
    'investment',
    'Investimento inicial',
    'Registrar reembolsos de aportes',
  ),
  permission(
    'investment.settings.manage',
    'investment',
    'Investimento inicial',
    'Gerenciar configurações do investimento',
  ),
  permission(
    'email_templates.read',
    'notifications',
    'Comunicação',
    'Visualizar modelos de e-mail',
  ),
  permission(
    'email_messages.read',
    'notifications',
    'Comunicação',
    'Ver a caixa de saída de e-mails',
  ),
  permission('email_templates.update', 'notifications', 'Comunicação', 'Editar modelos de e-mail'),
  permission(
    'email_templates.publish',
    'notifications',
    'Comunicação',
    'Publicar e reverter modelos de e-mail',
  ),
  permission('email_templates.test_send', 'notifications', 'Comunicação', 'Testar envio de e-mail'),
  permission(
    'whatsapp_templates.read',
    'notifications',
    'Comunicação',
    'Visualizar modelos de WhatsApp',
  ),
  permission(
    'whatsapp_templates.update',
    'notifications',
    'Comunicação',
    'Editar modelos de WhatsApp',
  ),
  permission(
    'financial_accounts.read',
    'finance',
    'Contas financeiras',
    'Visualizar contas financeiras',
  ),
  permission(
    'financial_accounts.create',
    'finance',
    'Contas financeiras',
    'Cadastrar contas financeiras',
  ),
  permission(
    'financial_accounts.update',
    'finance',
    'Contas financeiras',
    'Editar contas financeiras',
  ),
  permission(
    'financial_reports.read',
    'finance',
    'Relatórios das operadoras',
    'Consultar relatórios das operadoras',
  ),
  permission(
    'financial_reports.import',
    'finance',
    'Relatórios das operadoras',
    'Importar relatórios das operadoras',
  ),
  permission(
    'financial_reports.cancel',
    'finance',
    'Relatórios das operadoras',
    'Cancelar relatórios das operadoras',
  ),
  permission(
    'financial_reports.settings.manage',
    'finance',
    'Relatórios das operadoras',
    'Configurar disponibilização das operadoras',
  ),
  permission('financial_cards.use', 'finance', 'Cartão', 'Registrar pagamento em cartão parcelado'),
  permission(
    'financial_cards.settings.manage',
    'finance',
    'Cartão',
    'Configurar parcelamento e taxas da operadora',
  ),
  permission(
    'financial_accounts.archive',
    'finance',
    'Contas financeiras',
    'Arquivar e reativar contas financeiras',
  ),
  permission(
    'financial_movements.read',
    'finance',
    'Movimento financeiro',
    'Ver o extrato e o saldo das contas',
  ),
  permission(
    'financial_movements.create',
    'finance',
    'Movimento financeiro',
    'Lançar movimentos e a posição inicial',
  ),
  permission(
    'financial_movements.update',
    'finance',
    'Movimento financeiro',
    'Editar a descrição de movimentos',
  ),
  permission(
    'financial_movements.cancel',
    'finance',
    'Movimento financeiro',
    'Cancelar movimentos com motivo',
  ),
  permission(
    'financial_obligations.read',
    'finance',
    'Despesas e receitas',
    'Ver despesas e receitas',
  ),
  permission(
    'financial_obligations.create',
    'finance',
    'Despesas e receitas',
    'Cadastrar despesas e receitas',
  ),
  permission(
    'financial_obligations.update',
    'finance',
    'Despesas e receitas',
    'Editar despesas e receitas',
  ),
  permission(
    'financial_obligations.cancel',
    'finance',
    'Despesas e receitas',
    'Cancelar despesas e receitas',
  ),
  permission(
    'financial_reconciliation.read',
    'finance',
    'Conciliação',
    'Ver a conciliação do extrato com os compromissos',
  ),
  permission(
    'financial_reconciliation.settle',
    'finance',
    'Conciliação',
    'Conciliar pagamentos com compromissos e desfazer',
  ),
  permission(
    'financial_reconciliation.rules',
    'finance',
    'Conciliação',
    'Criar e editar regras de sugestão',
  ),
  permission(
    'financial_closing.read',
    'finance',
    'Fechamento',
    'Ver o fechamento financeiro do mês',
  ),
  permission('financial_closing.close', 'finance', 'Fechamento', 'Fechar o mês de caixa'),
  permission('financial_closing.reopen', 'finance', 'Fechamento', 'Reabrir o último mês fechado'),
  permission(
    'financial_recurrences.read',
    'finance',
    'Despesas recorrentes',
    'Ver despesas recorrentes',
  ),
  permission(
    'financial_recurrences.create',
    'finance',
    'Despesas recorrentes',
    'Cadastrar despesas recorrentes',
  ),
  permission(
    'financial_recurrences.update',
    'finance',
    'Despesas recorrentes',
    'Editar despesas recorrentes',
  ),
  permission(
    'financial_recurrences.archive',
    'finance',
    'Despesas recorrentes',
    'Arquivar e reativar despesas recorrentes',
  ),
  permission(
    'financial_recurrences.generate',
    'finance',
    'Despesas recorrentes',
    'Gerar as despesas do mês das recorrências',
  ),
  permission('accounting.chart.read', 'accounting', 'Plano de contas', 'Ver o plano de contas'),
  permission(
    'accounting.chart.manage',
    'accounting',
    'Plano de contas',
    'Criar, renomear, desativar e reativar contas',
  ),
  permission('accounting.classify', 'accounting', 'Classificação', 'Classificar lançamentos'),
  permission(
    'accounting.rules.manage',
    'accounting',
    'Classificação',
    'Criar, editar, aplicar e remover regras de classificação',
  ),
  permission('accounting.reports.read', 'accounting', 'Relatórios', 'Ver o DRE e o Fluxo de caixa'),
  permission(
    'accounting.closing.close',
    'accounting',
    'Fechamento',
    'Fechar o mês contábil e revisar valores contados duas vezes',
  ),
  permission(
    'accounting.closing.reopen',
    'accounting',
    'Fechamento',
    'Reabrir mês contábil fechado',
  ),
  permission(
    'accounting.closing.adjust',
    'accounting',
    'Fechamento',
    'Lançar ajuste de mês anterior',
  ),
]);

export type PermissionKey = (typeof permissionCatalog)[number]['key'];

const permissionByKey = new Map<string, PermissionDefinition>(
  permissionCatalog.map((item) => [item.key, item]),
);

export function findPermission(key: string): PermissionDefinition | undefined {
  return permissionByKey.get(key);
}
